import { getCategorizedRulesAndTotals } from '@fruitful/money-map';
import type {
    DeepPartial,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { MoneyMapRuleType, MoneyMapStatus } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import React, { type ReactNode, useEffect, useRef } from 'react';
import { H3Emphasized, SmoothieScrollView, XStack, YStack } from 'smoothie';

import { selectedMembershipAtom } from '../../entities/entityAtoms';
import { useMoneyMapAccounts } from '../providers/MoneyMapAccountsProvider';
import { type GoalPanelPreset } from '../utils/buildGoalPanelPresetGroupDto';
import { buildInitialMoneyMapGoalSetGroups } from '../utils/buildInitialMoneyMapGoalSetDtos';
import MoneyMapEditor from './editor/MoneyMapEditor';
import type { GoalSelectionState } from './goals-waterfall/goalsWaterfallTypes';
import type { GoalsWaterfallDnDControls } from './goals-waterfall/useGoalsWaterfallDnD';
import { MoneyMapperBillsTabPanel } from './MoneyMapperBillsTabPanel';
import { MoneyMapperCurrentAssetsSection } from './MoneyMapperCurrentAssetsSection';
import { MoneyMapperGoalsDevSection } from './MoneyMapperGoalsDevSection';
import { MoneyMapperInputCollectionSection } from './MoneyMapperInputCollectionSection';
import {
    type MoneyMapperPrimarySection,
    type MoneyMapperYourMoneySubSegment,
} from './moneyMapperNavigation';
import { MoneyMapperTakeHomePaySection } from './MoneyMapperTakeHomePaySection';
import { MoneyMapperYourMoneyTabBar } from './MoneyMapperYourMoneyTabBar';

export type {
    MoneyMapperPrimarySection,
    MoneyMapperYourMoneySubSegment,
} from './moneyMapperNavigation';

interface MoneyMapperInputPanelProps {
    /** Shown at the top-right of the grey content column (e.g. map toggle, publish). */
    headerTrailing?: ReactNode;
    primarySection: MoneyMapperPrimarySection;
    yourMoneySubSegment: MoneyMapperYourMoneySubSegment;
    onYourMoneySubSegmentChange: (
        segment: MoneyMapperYourMoneySubSegment,
    ) => void;
    isSaving: boolean;
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    onUpdateMoneyMap: (
        updates: DeepPartial<MoneyMapWithRulesAndAccounts>,
    ) => Promise<boolean>;
    /** Writes `monthlyIncomeCents` only; uses a separate mutation so `isSaving` stays false. */
    persistMonthlyIncomeCentsToMoneyMap: (
        monthlyIncomeCents: number,
    ) => Promise<boolean>;
    onRemoveRule: (ruleId: string) => Promise<void>;
    onRemoveGroup: (groupId: string) => Promise<void>;
    /** After seeding default goal sets, refetch so cache matches server (flat rules + groups). */
    refetchMoneyMap?: () => Promise<unknown>;
    onGoalSelected?: (selection: GoalSelectionState) => void;
    selectedGoal?: GoalSelectionState;
    goalsWaterfallDnd: GoalsWaterfallDnDControls;
    presetPreviewInsertIndex?: number | null;
    presetPreviewPreset?: GoalPanelPreset | null;
}

export const MoneyMapperInputPanel = ({
    headerTrailing,
    primarySection,
    yourMoneySubSegment,
    onYourMoneySubSegmentChange,
    isSaving,
    moneyMap,
    onUpdateMoneyMap,
    persistMonthlyIncomeCentsToMoneyMap,
    onRemoveRule,
    onRemoveGroup,
    refetchMoneyMap,
    onGoalSelected,
    selectedGoal,
    goalsWaterfallDnd,
    presetPreviewInsertIndex = null,
    presetPreviewPreset = null,
}: MoneyMapperInputPanelProps) => {
    const membership = useAtomValue(selectedMembershipAtom);

    const { accounts, refetch: refetchMoneyMapAccounts } =
        useMoneyMapAccounts();
    const requestedSeedRef = useRef<Set<string>>(new Set());
    const moneyMapId = moneyMap.id;

    useEffect(() => {
        requestedSeedRef.current = new Set();
    }, [moneyMapId]);

    useEffect(() => {
        if (
            (primarySection !== 'accounts' && primarySection !== 'goals') ||
            membership == null ||
            moneyMapId == null ||
            (moneyMap.status !== MoneyMapStatus.DRAFT &&
                moneyMap.status !== MoneyMapStatus.PREVIEW) ||
            isSaving
        ) {
            return;
        }

        const hasGoalGroups = (moneyMap.moneyMapRuleGroups?.length ?? 0) > 0;

        if (hasGoalGroups) {
            return;
        }

        const {
            billsTotalCents: currentMonthlyBillsCents,
            spendTotalCents: currentMonthlySpendCents,
        } = getCategorizedRulesAndTotals(moneyMap, accounts);

        if (requestedSeedRef.current.has(moneyMapId)) {
            return;
        }

        requestedSeedRef.current.add(moneyMapId);
        void (async () => {
            const moneyMapRulesWithoutGoals = (
                moneyMap.moneyMapRules ?? []
            ).filter((r) => r.type !== MoneyMapRuleType.GOAL);

            const ok = await onUpdateMoneyMap({
                moneyMapRules: moneyMapRulesWithoutGoals,
                moneyMapRuleGroups: buildInitialMoneyMapGoalSetGroups({
                    membership,
                    moneyMapId,
                    currentMonthlyBillsCents: currentMonthlyBillsCents ?? 0,
                    currentMonthlySpendCents: currentMonthlySpendCents ?? 0,
                }),
            });
            if (ok && refetchMoneyMap != null) {
                await refetchMoneyMap();
            }
            await refetchMoneyMapAccounts();
        })();
    }, [
        accounts,
        isSaving,
        membership,
        moneyMap,
        moneyMapId,
        primarySection,
        refetchMoneyMap,
        refetchMoneyMapAccounts,
        onUpdateMoneyMap,
    ]);

    const SECTION_TITLES: Record<typeof primarySection, string> = {
        'your-money': 'Inputs',
        goals: 'Goals',
        accounts: 'Accounts',
    };

    return (
        <YStack flex={1} backgroundColor="$gray100" overflow="hidden">
            <XStack
                justifyContent="space-between"
                alignItems="center"
                paddingHorizontal={24}
                paddingTop={16}
                paddingBottom={8}
                gap={16}
            >
                <H3Emphasized color="$black">
                    {SECTION_TITLES[primarySection]}
                </H3Emphasized>
                {headerTrailing != null && (
                    <XStack gap={16} alignItems="center" flexWrap="wrap">
                        {headerTrailing}
                    </XStack>
                )}
            </XStack>
            <YStack flex={1} paddingHorizontal={24} paddingTop={8}>
                {primarySection === 'goals' && (
                    <YStack flex={1} minHeight={0}>
                        <MoneyMapperGoalsDevSection
                            moneyMap={moneyMap}
                            goalsWaterfallDnd={goalsWaterfallDnd}
                            onGoalSelected={onGoalSelected}
                            selectedGoal={selectedGoal}
                            previewInsertIndex={presetPreviewInsertIndex}
                            previewPreset={presetPreviewPreset}
                        />
                    </YStack>
                )}
                {primarySection === 'accounts' && (
                    <YStack flex={1}>
                        <SmoothieScrollView>
                            <YStack
                                backgroundColor="$white"
                                borderRadius={24}
                                padding={24}
                            >
                                <MoneyMapEditor
                                    moneyMap={moneyMap}
                                    onUpdateMoneyMap={onUpdateMoneyMap}
                                    onRemoveRule={onRemoveRule}
                                    onRemoveGroup={onRemoveGroup}
                                    formPresentation="modal"
                                />
                            </YStack>
                        </SmoothieScrollView>
                    </YStack>
                )}
                {primarySection === 'your-money' && (
                    <YStack flex={1} minHeight={0} gap={20}>
                        <MoneyMapperYourMoneyTabBar
                            selected={yourMoneySubSegment}
                            onValueChange={onYourMoneySubSegmentChange}
                        />
                        <YStack flex={1} minHeight={0}>
                            {yourMoneySubSegment === 'take-home-pay' && (
                                <YStack flex={1} minHeight={0}>
                                    <MoneyMapperTakeHomePaySection
                                        isSaving={isSaving}
                                        moneyMap={moneyMap}
                                        onUpdateMoneyMap={onUpdateMoneyMap}
                                        persistMonthlyIncomeCentsToMoneyMap={
                                            persistMonthlyIncomeCentsToMoneyMap
                                        }
                                    />
                                </YStack>
                            )}
                            {yourMoneySubSegment === 'bills' && (
                                <YStack flex={1} minHeight={0}>
                                    <MoneyMapperBillsTabPanel
                                        accounts={accounts}
                                        isSaving={isSaving}
                                        membershipId={membership?.id ?? null}
                                        moneyMap={moneyMap}
                                        onUpdateMoneyMap={onUpdateMoneyMap}
                                    />
                                </YStack>
                            )}
                            {yourMoneySubSegment === 'current-assets' && (
                                <YStack flex={1} minHeight={0}>
                                    <MoneyMapperCurrentAssetsSection
                                        isSaving={isSaving}
                                    />
                                </YStack>
                            )}
                            {yourMoneySubSegment === 'spend' && (
                                <YStack flex={1} paddingBottom={24}>
                                    <MoneyMapperInputCollectionSection
                                        isSaving={isSaving}
                                        moneyMap={moneyMap}
                                        onUpdateMoneyMap={onUpdateMoneyMap}
                                        persistMonthlyIncomeCentsToMoneyMap={
                                            persistMonthlyIncomeCentsToMoneyMap
                                        }
                                    />
                                </YStack>
                            )}
                        </YStack>
                    </YStack>
                )}
            </YStack>
        </YStack>
    );
};
